import { create } from 'zustand'
import type { CardSummary, CardDetail, Domain, Stats } from '@/lib/api'

interface CardFilters {
  domain: string | null
  phase: string | null
  type: string | null
  search: string
  page: number
  pageSize: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

interface CardState {
  // Data
  cards: CardSummary[]
  totalCards: number
  hasMore: boolean
  selectedCard: CardDetail | null
  domains: Domain[]
  stats: Stats | null

  // Filters
  filters: CardFilters
  setFilter: <K extends keyof CardFilters>(key: K, value: CardFilters[K]) => void
  resetFilters: () => void

  // Loading states
  isLoading: boolean
  isLoadingCard: boolean
  error: string | null

  // Actions
  setCards: (cards: CardSummary[], total: number, hasMore: boolean) => void
  setSelectedCard: (card: CardDetail | null) => void
  setDomains: (domains: Domain[]) => void
  setStats: (stats: Stats) => void
  setLoading: (loading: boolean) => void
  setLoadingCard: (loading: boolean) => void
  setError: (error: string | null) => void
}

const defaultFilters: CardFilters = {
  domain: null,
  phase: null,
  type: null,
  search: '',
  page: 1,
  pageSize: 20,
  sortBy: 'created_at',
  sortOrder: 'desc',
}

export const useCardStore = create<CardState>((set) => ({
  cards: [],
  totalCards: 0,
  hasMore: false,
  selectedCard: null,
  domains: [],
  stats: null,

  filters: defaultFilters,
  setFilter: (key, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value,
        // Reset page when filters change (except page itself)
        page: key === 'page' ? (value as number) : 1,
      },
    })),
  resetFilters: () => set({ filters: defaultFilters }),

  isLoading: false,
  isLoadingCard: false,
  error: null,

  setCards: (cards, total, hasMore) =>
    set({ cards, totalCards: total, hasMore }),
  setSelectedCard: (card) => set({ selectedCard: card }),
  setDomains: (domains) => set({ domains }),
  setStats: (stats) => set({ stats }),
  setLoading: (loading) => set({ isLoading: loading }),
  setLoadingCard: (loading) => set({ isLoadingCard: loading }),
  setError: (error) => set({ error }),
}))

// Card type options
export const CARD_TYPES = [
  { value: 'claim', label: 'Claim' },
  { value: 'principle', label: 'Principle' },
  { value: 'tactic', label: 'Tactic' },
  { value: 'standard', label: 'Standard' },
  { value: 'pain_point', label: 'Pain Point' },
  { value: 'story_beat', label: 'Story Beat' },
  { value: 'question', label: 'Question' },
  { value: 'definition', label: 'Definition' },
  { value: 'analogy', label: 'Analogy' },
] as const

// Phase options (deprecated - kept for backward compatibility with old cards)
export const PHASES = [
  { value: 'collection', label: 'Collection' },
  { value: 'curation', label: 'Curation' },
  { value: 'contribution', label: 'Contribution' },
] as const
